import type { CustomStyleFunctionProps, MultiSelectProps } from '@usealloy/component-library';
import type { CSSObjectWithLabel } from 'react-select';
import type { Operation } from 'flow/service/changelog/types';
import { DIFF_COLOR_BY_OPERATION } from 'flow/service/graph';
import styled, { css } from 'styled-components';

/**
 * Shared diff-highlighting styles for fields inside the NodeEditorDrawer.
 *
 * Colors come from DIFF_COLOR_BY_OPERATION (flow/service/graph) so the drawer, the graph
 * nodes, and the threshold rows all key off one palette. Everything here takes an
 * Operation and returns styles, so a call site only needs the diff status of whatever
 * it is rendering.
 */

/** Tint strength for diff backgrounds, matching StyledThresholdContent. */
export const DIFF_TINT_STRENGTH = '20%';

export const GUTTER_TINT_STRENGTH = '40%';

/** Corner radius for a diff-colored control */
export const DIFF_CONTROL_RADIUS = 'var(--cl-radius-3)';

/**
 * Placeholder status for the drawer's field-level highlights.
 *
 * The styling is wired up at every field, but the per-field diff lookup that decides which
 * status each field should show is not built yet. null keeps the highlighting off so anyone
 * already using diff mode sees no change; set it to an Operation to preview the styling.
 */
export const PLACEHOLDER_DIFF_STATUS: Operation | null = null;

/** Returns the CSS color token for a given Operation. */
export const getDiffColor = (diffStatus: Operation) => DIFF_COLOR_BY_OPERATION[diffStatus];

/** Builds a translucent tint from a diff color token. */
export const getDiffTint = (color: string) => (
  `color-mix(in srgb, ${color} ${DIFF_TINT_STRENGTH}, transparent)`
);

/** Builds an opaque tint from a diff color token. */
export const getOpaqueDiffTint = (color: string) => (
  `color-mix(in srgb, ${color} ${DIFF_TINT_STRENGTH}, var(--cl-background-default))`
);

export const getOpaqueDiffTintAtStrength = (color: string, strength: string) => (
  `color-mix(in srgb, ${color} ${strength}, var(--cl-background-default))`
);

/** Returns MultiSelect style overrides that recolor the component background by diff status */
export function getDiffStyleFunction(
  diffStatus?: Operation | null,
): MultiSelectProps['customStyleFunctions'] {
  if (!diffStatus) return {};

  const color = getDiffColor(diffStatus);

  return {
    control: (styles: CSSObjectWithLabel) => ({
      ...styles,
      backgroundColor: getDiffTint(color),
      borderRadius: DIFF_CONTROL_RADIUS,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: color,
    }),
  };
}

/**
 * Returns per-chip overrides for a multi-item dropdown, coloring each selected chip by
 * its own diff status.
 *
 * `getChipDiffStatus` is called per chip with that chip's option value, so an individual
 * item can read as added/removed independently of the control as a whole. Chips whose
 * lookup returns null keep the library's default styling.
 */
export function getMultiItemDiffStyleFunctions(
  getChipDiffStatus: (value: unknown) => Operation | null,
): MultiSelectProps['customStyleFunctions'] {
  return {
    multiValue: (styles: CSSObjectWithLabel, props?: CustomStyleFunctionProps) => {
      const { data } = (props ?? {}) as { data?: unknown };
      const chipStatus = getChipDiffStatus(data);
      if (!chipStatus) return styles;

      const chipColor = getDiffColor(chipStatus);

      return {
        ...styles,
        backgroundColor: getOpaqueDiffTint(chipColor),
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: chipColor,
        borderRadius: 'var(--cl-space-1)',
      };
    },
  };
}

// NonNullable because customStyleFunctions is optional on MultiSelectProps; indexing a
// `T | undefined` union by a generic key collapses the value type to `never`.
type StyleFunctions = NonNullable<MultiSelectProps['customStyleFunctions']>;
type StyleFunction = NonNullable<StyleFunctions[keyof StyleFunctions]>;

/** Merges two sets of custom style functions so that both run. */
function mergeStyleFunctions(
  base: StyleFunctions = {},
  override: StyleFunctions = {},
): StyleFunctions {
  const merged: Record<string, StyleFunction> = { ...base };

  Object.entries(override).forEach(([key, overrideFn]: [string, StyleFunction]) => {
    const baseFn = merged[key];

    merged[key] = baseFn
      ? (styles: CSSObjectWithLabel, props?: CustomStyleFunctionProps) => (
        overrideFn(baseFn(styles, props), props)
      )
      : overrideFn;
  });

  return merged;
}

/** Merges diff coloring into an existing set of MultiSelect props */
export function withDiffStyles(
  multiSelectProps: Partial<MultiSelectProps>,
  diffStatus?: Operation | null,
): Partial<MultiSelectProps> {
  return {
    ...multiSelectProps,
    customStyleFunctions: mergeStyleFunctions(
      multiSelectProps.customStyleFunctions,
      getDiffStyleFunction(diffStatus),
    ),
  };
}

/** Merges multi-item diff coloring into an existing set of MultiSelect props. */
export function withMultiItemDiffStyles(
  multiSelectProps: Partial<MultiSelectProps>,
  getChipDiffStatus: (value: unknown) => Operation | null,
): Partial<MultiSelectProps> {
  return {
    ...multiSelectProps,
    customStyleFunctions: mergeStyleFunctions(
      multiSelectProps.customStyleFunctions,
      getMultiItemDiffStyleFunctions(getChipDiffStatus),
    ),
  };
}

export const StyledCheckbox = styled.div<{ diffStatus?: Operation | null }>`
  ${({ diffStatus }) => {
    if (!diffStatus) return null;
    const color = getDiffColor(diffStatus);

    return css`
      &&& input[type='checkbox'] + span {
        border: 2px solid ${color};
        background-color: ${getDiffTint(color)};
      }

      &&& input[type='checkbox']:checked + span {
        background-color: ${getOpaqueDiffTint(color)};
      }

      /* The library's white check would disappear on the pale tint. Its icon paths are
         fill="currentColor" and its color comes from an SVG attribute, so CSS wins here. */
      &&& input[type='checkbox'] + span svg {
        color: ${color};
        fill: ${color};
      }
    `;
  }}
`;

export const StyledTextField = styled.div<{ diffStatus?: Operation | null }>`
  /* Layout-transparent: see StyledTextAreaWrapper. Column parents stretch a block div anyway,
     but inside a row-direction flex parent it would otherwise shrink to content. */
  display: flex;
  flex: 1;
  width: 100%;
  min-width: 0;

  > * {
    width: 100%;
  }

  ${({ diffStatus }) => {
    if (!diffStatus) return null;
    const color = getDiffColor(diffStatus);

    return css`
      &&& input {
        border: 2px solid ${color};
        background-color: ${getOpaqueDiffTint(color)};
        opacity: 1;
        color: var(--cl-text-muted);
      }
    `;
  }}
`;

export const StyledPicker = styled.div<{ diffStatus?: Operation | null }>`
  ${({ diffStatus }) => {
    if (!diffStatus) return null;
    const color = getDiffColor(diffStatus);

    return css`
      &&& button,
      &&& button:disabled {
        border: 2px solid ${color};
        background-color: ${getOpaqueDiffTint(color)};
        border-color: ${color};
        opacity: 1;
        color: var(--cl-text-muted);
      }
    `;
  }}
`;

export const StyledTextAreaWrapper = styled.div<{ diffStatus?: Operation | null }>`
  /* This wrapper exists only to scope the diff styles below, so it must not disturb the
     layout it is inserted into. TextArea is built to stretch as a flex child (its own root is
     width: 100%), and a plain block div between the flex parent and the field would size to
     content and collapse it. Stretching in both axes keeps the wrapper transparent whether
     the parent lays out in a row or a column. */
  display: flex;
  flex: 1;
  width: 100%;
  min-width: 0;

  > * {
    width: 100%;
  }

  &&& textarea {
    resize: vertical;
    line-height: 1.5;
  }

  ${({ diffStatus }) => {
    if (!diffStatus) return null;
    const color = getDiffColor(diffStatus);

    return css`
      &&& textarea,
      &&& textarea:disabled {
        background-color: ${getOpaqueDiffTint(color)};
        opacity: 1;
        color: var(--cl-text-muted);
      }

      &&& textarea {
        border: 2px solid ${color};
        border-radius: ${DIFF_CONTROL_RADIUS};
      }

      &&& div:has(> div + textarea) {
        border: 2px solid ${color};
      }

      &&& div > div + textarea {
        border: none;
        border-radius: 0 ${DIFF_CONTROL_RADIUS} ${DIFF_CONTROL_RADIUS} 0;
      }

      &&& div:has(> textarea) > div:first-child {
        background: ${getOpaqueDiffTintAtStrength(color, GUTTER_TINT_STRENGTH)} !important;
        color: var(--cl-text-muted) !important;
        border-top-left-radius: calc(${DIFF_CONTROL_RADIUS} - 2px) !important;
        border-bottom-left-radius: calc(${DIFF_CONTROL_RADIUS} - 2px) !important;
      }
    `;
  }}
`;
